import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ToastController } from '@ionic/angular/standalone';

import { addIcons } from 'ionicons';

import { checkmarkCircleOutline, closeCircleOutline } from 'ionicons/icons';

export enum ToastStatus {
  Neutral = 'neutral',
  Error = 'error',
  Success = 'success',
}

type UtmKey =
  | 'utm_source'
  | 'utm_medium'
  | 'utm_campaign'
  | 'utm_content'
  | 'utm_term';

type UtmParams = Partial<Record<UtmKey, string>>;

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private readonly utmStorageKey = 'utm_params';
  private readonly utmKeys: UtmKey[] = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ];

  constructor(private toastController: ToastController, private activatedRoute: ActivatedRoute) {
    addIcons({ closeCircleOutline, checkmarkCircleOutline });
  }

  public setStorage(key: string, valor: any): void {
    localStorage.setItem(`institucional-trigo.${key}`, JSON.stringify(valor));
  }

  public getStorage(key: string | null): any {
    if (key === null) return null;

    const storedItem = localStorage.getItem(`institucional-trigo.${key}`);

    if (storedItem !== null) return JSON.parse(storedItem);
    else return null;
  }

  public removeStorage(key: string): void {
    localStorage.removeItem(`institucional-trigo.${key}`);
  }

  public captureUtmParams(url: string = window.location.href): UtmParams {
    const utmParams = this.extractUtmParams(url);

    if (Object.keys(utmParams).length > 0) {
      this.setStorage(this.utmStorageKey, utmParams);
      return utmParams;
    }

    return this.getStoredUtmParams();
  }

  public getUtmParams(): UtmParams {
    const currentUrlParams = this.extractUtmParams(window.location.href);

    if (Object.keys(currentUrlParams).length > 0) {
      this.setStorage(this.utmStorageKey, currentUrlParams);
      return currentUrlParams;
    }

    return this.getStoredUtmParams();
  }

  public pushDataLayerEvent(event: string, data: Record<string, any> = {}): void {
    try {
      const w = window as any;
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({
        event,
        ...this.getUtmParams(),
        ...data,
      });
    } catch (_) {
      // no-op
    }
  }

  public trackFranchiseeEntryCta(location: string, data: Record<string, any> = {}): void {
    this.pushDataLayerEvent('cta_click', {
      cta_name: 'seja_nosso_franqueado',
      cta_text: 'Seja nosso franqueado!',
      cta_location: location,
      funnel_step: 'entry',
      ...data,
    });
  }

  public trackFranchiseeFormStep(
    step: 'continuar' | 'conclusao',
    data: Record<string, any> = {}
  ): void {
    const funnelStep = step === 'continuar' ? 'qualification' : 'conversion';

    this.pushDataLayerEvent('form_submit', {
      cta_name: step === 'continuar' ? 'continuar' : 'seja_nosso_franqueado',
      funnel_step: funnelStep,
      form_step: step,
      ...data,
    });
  }

  /**
   * @description Exibe um toast na tela.
   * @param message
   * @param status
   * @param duration
   * @returns
   */
  public async toast(
    message: string,
    status: ToastStatus = ToastStatus.Neutral,
    duration: number = 1500
  ): Promise<void> {
    const statusMappings: Record<string, { icon: string; color: string }> = {
      neutral: { icon: '', color: 'warning' },
      error: { icon: 'close-circle-outline', color: 'danger' },
      success: { icon: 'checkmark-circle-outline', color: 'success' },
    };

    const lowerCaseStatus = status.toLowerCase();
    const { icon, color } = statusMappings[lowerCaseStatus] || {
      icon: '',
      color: 'warning',
    };

    const toast = await this.toastController.create({
      position: 'top',
      message: message,
      cssClass: 'toast-' + lowerCaseStatus,
      duration: duration,
      color: color,
    });

    if (icon !== '') toast.icon = icon;

    return await toast.present();
  }

  /**
   * @description Esta função é útil para construção e validação de textos que não podem conter caracteres especiais, espaços e distinção de tamanho (maiúsculo ou minúsculo)
   * @param text
   * @returns Texto sem nenhum caractere especial, em minúsculo, separado por traços.
   */
  public validUri(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\W_]+/g, '-')
      .toLowerCase()
      .replace(/^-+|-+$/g, '');
  }

  //Transformar data ISO em sql datetime
  public formatDateTime(datetime: string): string {
    if (!datetime) return '';
    
    try {
      const date = new Date(datetime);
      
      if (isNaN(date.getTime())) {
        return '';
      }
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '';
    }
  }

  /**
   * Corrige URLs que vêm da API com barras escapadas (\/\/ -> //)
   */
  public sanitizeUrl(url: string | undefined | null): string {
    if (!url || typeof url !== 'string') return '';
    return url.replace(/\\\//g, '/');
  }

  public getYoutubeId(url: string) {
      // Try using URL API first for robustness
      try {
        const u = new URL(url);
        const host = u.hostname.replace('www.', '');

        const vParam = u.searchParams.get('v');
        if (vParam) return vParam;

        const path = u.pathname;
        const embedMatch = path.match(/\/embed\/([^?&#/]+)/);
        if (embedMatch && embedMatch[1]) return embedMatch[1];

        const shortsMatch = path.match(/\/shorts\/([^?&#/]+)/);
        if (shortsMatch && shortsMatch[1]) return shortsMatch[1];

        const liveMatch = path.match(/\/live\/([^?&#/]+)/);
        if (liveMatch && liveMatch[1]) return liveMatch[1];

        if (host === 'youtu.be') {
          const idFromPath = path.replace(/^\/+/, '').split('/')[0];
          if (idFromPath) return idFromPath;
        }
      } catch {}

      // Fallback regex checks (covers nocookie domains as well)
      const patterns = [
        /youtu\.be\/([^?&#/]+)/,
        /youtube(?:-nocookie)?\.com\/embed\/([^?&#/]+)/,
        /youtube(?:-nocookie)?\.com\/shorts\/([^?&#/]+)/,
        /youtube(?:-nocookie)?\.com\/live\/([^?&#/]+)/,
        /[?&]v=([^?&#/]+)/,
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) return match[1];
      }
      return null;
   }

  public handleFragment(): void {
    this.activatedRoute.fragment.subscribe(fragment => {
      if (fragment) {
        setTimeout(() => {
          this.scrollToElement(fragment);
        }, 500);
      }
    });
  }

  private scrollToElement(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 1000);
    }
  }

  private getStoredUtmParams(): UtmParams {
    return (this.getStorage(this.utmStorageKey) as UtmParams) || {};
  }

  private extractUtmParams(url: string): UtmParams {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const utmParams: UtmParams = {};

      this.utmKeys.forEach((key) => {
        const value = parsedUrl.searchParams.get(key);

        if (value && value.trim() !== '') {
          utmParams[key] = this.normalizeUtmValue(value);
        }
      });

      return utmParams;
    } catch {
      return {};
    }
  }

  private normalizeUtmValue(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, '_');
  }

  public removeHtml(html: string): string {
    return html.replace(/<[^>]*>?/g, '');
  }
}


